import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../components/shared/Notifications/useNotifications";
import { IDefaultResponse } from "../../models/DefaultResponse";
import {
  IAiResultFilterPrompt,
  IInitialPriceEstimateRequest,
  IInitialPriceEstimateResponse,
  ISaveAiResultFilterPromptRequest,
} from "../../models/Category/InitialPriceEstimate";
import { InitialPriceEstimateQueryKeys } from "./keys";

export default function useInitialPriceEstimateMutations() {
  const { put, post, del } = useApiHelper();
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Price estimate configuration");
  const { addNotification } = useNotification();

  const invalidateAll = () =>
    queryClient.invalidateQueries({
      queryKey: InitialPriceEstimateQueryKeys.all(),
    });

  /** Adds categories to the priced list with default settings. */
  const create = useMutation({
    mutationFn: (partCategoryIds: string[]) =>
      post<IDefaultResponse<IInitialPriceEstimateResponse[]>>(
        `/initial-price-estimate/categories`,
        { partCategoryIds },
      ),
    onSuccess: (res) => {
      addNotification({
        variant: "success",
        primaryText: res.message || "Categories added",
      });
      invalidateAll();
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to add categories",
      });
    },
  });

  const remove = useMutation({
    mutationFn: (partCategoryId: string) =>
      del<IDefaultResponse<null>>(
        `/initial-price-estimate/categories/${partCategoryId}`,
      ),
    onSuccess: () => {
      addNotification({
        variant: "success",
        primaryText: "Category removed from price estimates",
      });
      invalidateAll();
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to remove the category",
      });
    },
  });

  const save = useMutation({
    mutationFn: ({
      partCategoryId,
      config,
    }: {
      partCategoryId: string;
      config: IInitialPriceEstimateRequest;
    }) =>
      put<IDefaultResponse<IInitialPriceEstimateResponse>>(
        `/initial-price-estimate/categories/${partCategoryId}`,
        config,
      ),
    onSuccess: () => {
      saveHandlers.onSuccess();
      // The list shows the keyword, sort order and active state, so it goes stale too.
      invalidateAll();
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText:
          error.message || "Failed to save the price estimate configuration",
      });
    },
  });

  /** The default AI match prompt. Live on the next lookup; no category sync needed. */
  const saveAiPrompt = useMutation({
    mutationFn: (request: ISaveAiResultFilterPromptRequest) =>
      put<IDefaultResponse<IAiResultFilterPrompt>, ISaveAiResultFilterPromptRequest>(
        `/initial-price-estimate/ai-prompt`,
        request,
      ),
    onSuccess: (res) => {
      addNotification({
        variant: "success",
        primaryText: res.message || "AI prompt saved",
      });
      // Every category's "default prompt" preview reads from this, so the lot goes stale.
      invalidateAll();
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to save the AI prompt",
      });
    },
  });

  return { create, remove, save, saveAiPrompt };
}
